import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard, { toneBadge } from '@/components/feature/ArticleCard';
import ContentBlocks from '@/pages/article/components/ContentBlocks';
import ArticleToc, { type TocItem } from '@/pages/article/components/ArticleToc';
import NotFound from '@/pages/NotFound';
import { SITE } from '@/data/site';
import { getCategory } from '@/data/categories';
import { articles, getArticle, getRelated } from '@/data/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticle(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  const category = getCategory(article.category);
  const related = getRelated(article, 3);
  const index = articles.findIndex((a) => a.slug === article.slug);
  const prev = index > 0 ? articles[index - 1] : null;
  const next = index < articles.length - 1 ? articles[index + 1] : null;

  const toc: TocItem[] = article.content
    .map((block, i) => (block.type === 'h2' ? { id: `section-${i}`, text: block.text } : null))
    .filter((x): x is TocItem => Boolean(x));

  const path = `/article/${article.slug}`;

  const schema: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt,
      inLanguage: 'zh-CN',
      author: { '@type': 'Organization', name: article.author },
      publisher: { '@type': 'Organization', name: SITE.name, url: SITE.domain },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE.domain}${path}` },
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE.domain },
        ...(category
          ? [
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `${SITE.domain}/category/${category.slug}`,
              },
            ]
          : []),
        {
          '@type': 'ListItem',
          position: category ? 3 : 2,
          name: article.title,
          item: `${SITE.domain}${path}`,
        },
      ],
    },
  ];

  if (article.faq && article.faq.length > 0) {
    schema.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <SiteLayout>
      <Seo
        title={article.seoTitle}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},${category?.name ?? ''},吃瓜网`}
        path={path}
        image={article.cover}
        type="article"
        schema={schema}
      />

      <div className="max-w-container mx-auto px-4 md:px-8 pt-8">
        <Breadcrumbs
          items={[
            { label: '首页', path: '/' },
            ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
            { label: article.title },
          ]}
        />
      </div>

      <article className="max-w-container mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* 目录 */}
          <aside className="hidden lg:block lg:col-span-3 order-2 lg:order-1">
            <div className="sticky top-24 space-y-6">
              <ArticleToc items={toc} />
              {category && (
                <div className={`rounded-lg p-4 ${toneBadge[category.accent]}`}>
                  <p className="text-sm font-medium">{category.name}</p>
                  <p className="mt-1.5 text-xs leading-6 opacity-80">{category.tagline}</p>
                  <Link
                    to={`/category/${category.slug}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  >
                    返回栏目
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </aside>

          {/* 正文 */}
          <div className="lg:col-span-9 order-1 lg:order-2">
            <div className="max-w-3xl">
              <header>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${toneBadge[category.accent]}`}
                  >
                    {category.name}
                  </Link>
                )}
                <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug tracking-tight">
                  {article.title}
                </h1>
                <p className="mt-5 text-base md:text-lg leading-8 text-foreground-600 border-l-2 border-primary-300 pl-4">
                  {article.lead}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 pb-6 border-b border-background-200">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line" />
                    </span>
                    {article.author}
                  </span>
                  <time dateTime={article.publishedAt} className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line" />
                    </span>
                    发布于 {article.publishedAt}
                  </time>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line" />
                    </span>
                    约 {article.readingTime} 分钟阅读
                  </span>
                  <span className="text-foreground-400">更新于 {article.updatedAt}</span>
                </div>
              </header>

              <div className="mt-7 rounded-lg overflow-hidden bg-background-200 h-56 md:h-80">
                <img
                  src={article.cover}
                  alt={article.title}
                  width={1200}
                  height={800}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-9">
                <ContentBlocks blocks={article.content} />
              </div>

              {/* 标签 */}
              <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">相关标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              {/* FAQ */}
              {article.faq && article.faq.length > 0 && (
                <section className="mt-12">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">
                    常见问题
                  </h2>
                  <div className="space-y-3">
                    {article.faq.map((f) => (
                      <details
                        key={f.q}
                        className="group rounded-lg border border-background-200 bg-background-50 px-5 py-4"
                      >
                        <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                          <span className="font-medium text-sm text-foreground-900">{f.q}</span>
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                            <i className="ri-add-line text-lg" />
                          </span>
                        </summary>
                        <p className="mt-3 text-sm leading-7 text-foreground-600">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {/* 上下篇 */}
              <nav className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="上一篇与下一篇">
                {prev ? (
                  <Link
                    to={`/article/${prev.slug}`}
                    className="group p-5 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-left-line" />
                      </span>
                      上一篇
                    </span>
                    <span className="mt-2 block font-heading text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {prev.title}
                    </span>
                  </Link>
                ) : (
                  <div className="hidden sm:block" />
                )}
                {next && (
                  <Link
                    to={`/article/${next.slug}`}
                    className="group p-5 rounded-lg border border-background-200 hover:border-primary-300 transition-colors sm:text-right"
                  >
                    <span className="text-xs text-foreground-500 flex items-center gap-1.5 sm:justify-end">
                      下一篇
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </span>
                    <span className="mt-2 block font-heading text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {next.title}
                    </span>
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </div>
      </article>

      {/* 相关推荐 */}
      <section className="max-w-container mx-auto px-4 md:px-8 pb-6">
        <div className="border-t border-background-200 pt-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              继续读下去
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}